import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssetTCO = ({ asset }) => {
  const { t } = useTranslation();
  const [tco, setTco] = useState(null);

  useEffect(() => {
    const fetchTco = async () => {
      try {
        const r = await axios.get(`${API}/inventory/assets/${asset.id}/tco`);
        setTco(r.data);
      } catch {}
    };
    fetchTco();
  }, [asset.id]);

  const handleExportPdf = async () => {
    try {
      const r = await axios.get(`${API}/inventory/assets/${asset.id}/export/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(`${asset.inventory_number}_report.pdf`);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('inv.tco.exported'));
    } catch { toast.error(t('inv.tco.exportFailed')); }
  };

  if (!tco) return null;

  const Row = ({ label, value, bold }) => (
    <div className={`flex justify-between py-1 ${bold ? 'border-t pt-2 mt-1' : ''}`}>
      <span className={`text-xs ${bold ? 'font-semibold' : 'text-muted-foreground'}`}>{label}</span>
      <span className={`text-xs ${bold ? 'font-bold text-red-700' : 'font-medium'}`}>{value}</span>
    </div>
  );

  const fmt = (v) => `${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${tco.currency}`;

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="asset-tco-card">
      <CardContent className="pt-3 pb-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase text-muted-foreground">{t('inv.tco.title')}</h4>
          <DocLangToggle testId="doc-lang-assettco-0" />
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleExportPdf}
            title={t('inv.tco.exportPdf')} data-testid="export-asset-pdf-btn">
            <FileText className="w-3.5 h-3.5" />
          </Button>
        </div>
        <Row label={t('inv.tco.purchaseValue')} value={fmt(tco.purchase_value)} />
        {tco.vat_value > 0 && <Row label={t('inv.tco.vat')} value={fmt(tco.vat_value)} />}
        <Row label={t('inv.tco.depreciation', { count: tco.depreciation_entries_count })}
          value={`-${fmt(tco.total_depreciation)}`} />
        <Row label={t('inv.tco.bookValue')} value={fmt(tco.book_value)} />
        <Row label={t('inv.tco.runningCosts', { count: tco.linked_expense_count })} value={fmt(tco.linked_expenses)} />
        <Row label={t('inv.tco.repairCosts', { count: tco.damage_report_count })} value={fmt(tco.repair_costs)} />
        <Row label={t('inv.tco.title')} value={fmt(tco.total_cost_of_ownership)} bold />
      </CardContent>
    </Card>
  );
};

export default AssetTCO;
